import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDatabase } from '@/contexts/DatabaseContext';
import { useToast } from '@/contexts/ToastContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

type PendingAction = 'restore' | 'importDrivers' | 'importTrucks' | 'reset' | null;

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const importRef = useRef<HTMLDivElement>(null);
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const driversExcelInputRef = useRef<HTMLInputElement>(null);
  const trucksExcelInputRef = useRef<HTMLInputElement>(null);
  const {
    currentUser,
    notifications,
    unreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification,
    logout,
    exportDatabase,
    importDatabase,
    importDriversFromExcel,
    importTrucksFromExcel,
    importDriversFromCSV,
    importTrucksFromCSV,
    verifyPassword,
    resetDatabase,
  } = useDatabase();

  // ─── Tab notification counter ────────────────────
  useEffect(() => {
    if (unreadNotificationCount > 0) {
      document.title = `(${unreadNotificationCount}) SILY-OBC`;
    } else {
      document.title = 'SILY-OBC';
    }
  }, [unreadNotificationCount]);

  // ─── Click outside handlers ──────────────────────
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (importRef.current && !importRef.current.contains(event.target as Node)) {
        setImportOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return t('dashboard');
    if (path.startsWith('/tasks')) return t('tasks');
    if (path.startsWith('/trucks')) return t('manageTrucks');
    if (path.startsWith('/drivers')) return t('manageDrivers');
    if (path.startsWith('/profile')) return t('myProfile');
    return t('dashboard');
  };

  const formatNotifDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'task_created':
        return 'ri-add-circle-line text-primary-600';
      case 'task_status':
        return 'ri-arrow-right-circle-line text-accent-600';
      case 'task_deleted':
        return 'ri-delete-bin-line text-accent-600';
      default:
        return 'ri-notification-3-line text-foreground-500';
    }
  };

  const recentNotifications = notifications.slice(0, 8);

  // ─── Password confirmation for imports & reset ───

  const requestAction = (action: PendingAction) => {
    setPendingAction(action);
    setPasswordInput('');
    setPasswordError('');
    setShowConfirmPassword(false);
    setPasswordModalOpen(true);
  };

  const confirmPassword = async () => {
    const valid = await verifyPassword(passwordInput);
    if (valid) {
      setPasswordModalOpen(false);
      setPasswordInput('');
      setPasswordError('');
      // Small delay to let the modal unmount before triggering file input
      setTimeout(() => {
        executePendingAction();
      }, 100);
    } else {
      setPasswordError(t('invalidPassword') || 'Mot de passe incorrect.');
    }
  };

  const executePendingAction = () => {
    switch (pendingAction) {
      case 'restore':
        restoreInputRef.current?.click();
        break;
      case 'importDrivers':
        driversExcelInputRef.current?.click();
        break;
      case 'importTrucks':
        trucksExcelInputRef.current?.click();
        break;
      case 'reset':
        resetDatabase();
        break;
      default:
        break;
    }
    setPendingAction(null);
  };

  const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      confirmPassword();
    }
  };

  // ─── Import handlers ─────────────────────────────
  // Success/error toasts for these are shown by DatabaseContext itself, tied
  // to the actual db.json write — nothing to do here on success.

  const handleRestoreJSON = () => {
    requestAction('restore');
  };

  const handleRestoreFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const result = await importDatabase(reader.result as string);
      if (!result.success && result.message) showToast(result.message, 'error');
    };
    reader.onerror = () => showToast('Erreur lors de la lecture du fichier.', 'error');
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportDriversExcel = () => {
    requestAction('importDrivers');
  };

  const handleDriversExcelFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const extension = file.name.split('.').pop()?.toLowerCase();
    const result = extension === 'csv' ? await importDriversFromCSV(file) : await importDriversFromExcel(file);
    if (!result.success && result.message) showToast(result.message, 'error');
    e.target.value = '';
  };

  const handleImportTrucksExcel = () => {
    requestAction('importTrucks');
  };

  const handleTrucksExcelFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const extension = file.name.split('.').pop()?.toLowerCase();
    const result = extension === 'csv' ? await importTrucksFromCSV(file) : await importTrucksFromExcel(file);
    if (!result.success && result.message) showToast(result.message, 'error');
    e.target.value = '';
  };

  // ─── Danger zone: wipe all data ───────────────────

  const handleResetDatabase = () => {
    const confirmed = window.confirm(
      '⚠️ Cette action va supprimer DÉFINITIVEMENT tous les camions, chauffeurs, tâches, notifications et commentaires de la base de données (les comptes utilisateurs sont conservés). Continuer ?'
    );
    if (confirmed) requestAction('reset');
  };

  // ─── Nav items ───────────────────────────────────

  const navItems = [
    { path: '/', icon: 'ri-dashboard-line', labelKey: 'dashboard' },
    { path: '/tasks', icon: 'ri-task-line', labelKey: 'tasks' },
    { path: '/trucks', icon: 'ri-truck-line', labelKey: 'manageTrucks' },
    { path: '/drivers', icon: 'ri-user-star-line', labelKey: 'manageDrivers' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hidden file inputs */}
      <input
        ref={restoreInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleRestoreFile}
      />
      <input
        ref={driversExcelInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleDriversExcelFile}
      />
      <input
        ref={trucksExcelInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleTrucksExcelFile}
      />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — fixed on all screen sizes */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 h-screen bg-background-100 border-r border-background-200 flex flex-col overflow-y-auto transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-background-200 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center mr-3">
            <i className="ri-truck-line text-background-50 text-lg" />
          </div>
          <span className="font-heading font-bold text-lg text-foreground-950 whitespace-nowrap">
            SILY-OBC
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/'));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-3">
                  <i className={`${item.icon} text-base`} />
                </div>
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* Export / Import section */}
        <div className="px-4 pb-1">
          <div className="relative" ref={importRef}>
            <button
              onClick={() => setImportOpen(!importOpen)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-5 h-5 flex items-center justify-center mr-3">
                  <i className="ri-folder-transfer-line text-base" />
                </div>
                {t('importExport')}
              </div>
              <i className={`${importOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs text-foreground-400`} />
            </button>

            {importOpen && (
              <div className="mx-0 mb-1 bg-background-200/40 rounded-lg p-1 space-y-0.5">
                <button
                  onClick={() => { exportDatabase(); setImportOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-xs text-foreground-700 hover:bg-background-200 transition-colors text-left"
                >
                  <i className="ri-download-2-line mr-2 text-primary-600" />
                  {t('exportDatabase')}
                </button>
                <button
                  onClick={() => { handleRestoreJSON(); setImportOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-xs text-foreground-700 hover:bg-background-200 transition-colors text-left"
                >
                  <i className="ri-upload-2-line mr-2 text-accent-600" />
                  {t('restoreDatabase')}
                </button>
                <div className="border-t border-background-300/60 my-1" />
                <button
                  onClick={() => { handleImportDriversExcel(); setImportOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-xs text-foreground-700 hover:bg-background-200 transition-colors text-left"
                >
                  <i className="ri-file-excel-2-line mr-2 text-primary-600" />
                  {t('importDrivers')}
                </button>
                <button
                  onClick={() => { handleImportTrucksExcel(); setImportOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-xs text-foreground-700 hover:bg-background-200 transition-colors text-left"
                >
                  <i className="ri-file-excel-2-line mr-2 text-primary-600" />
                  {t('importTrucks')}
                </button>
                <div className="border-t border-background-300/60 my-1" />
                <button
                  onClick={() => { handleResetDatabase(); setImportOpen(false); }}
                  className="w-full flex items-center px-3 py-2 rounded-md text-xs text-accent-700 hover:bg-accent-50 transition-colors text-left"
                >
                  <i className="ri-delete-bin-2-line mr-2 text-accent-600" />
                  {t('resetDatabase')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* User info at bottom */}
        <div className="p-4 border-t border-background-200 shrink-0">
          <div className="flex items-center px-2">
            <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-semibold text-sm shrink-0">
              {currentUser.fullName.charAt(0)}
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-foreground-900 truncate">
                {currentUser.fullName}
              </p>
              <p className="text-xs text-foreground-500 truncate">
                {t(currentUser.role)}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content — offset by sidebar width on desktop */}
      <div className="lg:ml-64 flex flex-col min-h-screen">
        {/* Top navbar */}
        <header className="h-16 bg-background-50 border-b border-background-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <h1 className="ml-3 lg:ml-0 font-heading font-semibold text-foreground-900 text-base md:text-lg">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600"
              >
                <i className="ri-notification-3-line text-lg" />
                {unreadNotificationCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[10px] font-bold px-1">
                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 md:w-96 bg-background-50 rounded-xl border border-background-200 shadow-lg z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                    <span className="font-semibold text-sm text-foreground-900">
                      {t('notifications')}
                    </span>
                    {unreadNotificationCount > 0 && (
                      <button
                        onClick={markAllNotificationsRead}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {t('markAllRead')}
                      </button>
                    )}
                  </div>

                  <div className="max-h-[360px] overflow-y-auto">
                    {recentNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-background-100 flex items-center justify-center">
                          <i className="ri-notification-off-line text-foreground-400 text-lg" />
                        </div>
                        <p className="text-sm text-foreground-500">{t('noNotifications')}</p>
                      </div>
                    ) : (
                      recentNotifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`flex items-start gap-3 px-4 py-3 border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer ${
                            !notif.isRead ? 'bg-secondary-50/60' : ''
                          }`}
                          onClick={() => {
                            markNotificationRead(notif.id);
                            if (notif.taskId) {
                              setNotifOpen(false);
                              navigate('/tasks');
                            }
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center shrink-0 mt-0.5">
                            <i className={getNotifIcon(notif.type)} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-foreground-900 truncate">
                                {notif.title}
                              </p>
                              {!notif.isRead && (
                                <span className="w-2 h-2 rounded-full bg-accent-500 shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-foreground-600 mt-0.5 leading-snug line-clamp-2">
                              {notif.message}
                            </p>
                            <span className="text-[10px] text-foreground-400 mt-1 block">
                              {formatNotifDate(notif.createdAt)}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notif.id);
                            }}
                            className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-background-200 text-foreground-400 hover:text-accent-600 shrink-0"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {notifications.length > 8 && (
                    <div className="px-4 py-2.5 border-t border-background-200 text-center">
                      <span className="text-xs text-foreground-400">
                        +{notifications.length - 8} {t('moreNotifications')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-background-100"
              >
                <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-semibold text-sm">
                  {currentUser.fullName.charAt(0)}
                </div>
                <span className="hidden md:block text-sm font-medium text-foreground-800">
                  {currentUser.fullName}
                </span>
                <i className="ri-arrow-down-s-line text-foreground-500" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-background-50 rounded-lg border border-background-200 shadow-lg z-50 py-1">
                  <div className="px-4 py-2.5 border-b border-background-100">
                    <p className="text-sm font-medium text-foreground-900">{currentUser.fullName}</p>
                    <p className="text-xs text-foreground-500">{currentUser.email}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-user-settings-line mr-2" />
                    {t('myProfile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-accent-700 hover:bg-accent-50 text-left"
                  >
                    <i className="ri-logout-box-r-line mr-2" />
                    {t('logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Password confirmation modal */}
      {passwordModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => { setPasswordModalOpen(false); setPendingAction(null); }}
          />
          <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-foreground-950 font-heading mb-1">
              {t('confirmPassword')}
            </h2>
            <p className="text-sm text-foreground-500 mb-4">
              {t('enterPasswordToConfirm')}
            </p>

            {passwordError && (
              <div className="mb-3 p-2.5 rounded-lg bg-accent-50 text-accent-800 text-xs">
                {passwordError}
              </div>
            )}

            <div className="mb-4">
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(''); }}
                  onKeyDown={handlePasswordKeyDown}
                  placeholder="••••••••"
                  autoFocus
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors"
                  tabIndex={-1}
                >
                  <i className={`${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setPasswordModalOpen(false); setPendingAction(null); }}
                className="flex-1 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmPassword}
                disabled={!passwordInput}
                className="flex-1 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
